import { useEffect, useRef, useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  hasSkew,
  isSafeToReload,
  hasAutoReloadFiredForBuild,
  markAutoReloadFiredForBuild,
  RELOAD_COUNTDOWN_SECONDS,
  AUTO_RELOAD_CHECK_INTERVAL_MS,
  NOTIFY_CHECK_INTERVAL_MS,
  NOTIFY_REPEAT_MS,
  FOCUS_DEBOUNCE_MS,
} from '@/lib/version-skew';

/**
 * Detects when a newer frontend build has been deployed while this tab has
 * stayed open, and reacts to it.
 *
 * Why: deploys ship several times a week; a tab loaded before a deploy keeps
 * running the old bundle against the new API. When an enum or contract
 * changes, that tab hard-fails with a cryptic validation error (real
 * incident: the product-type rename broke record creation for a stale tab).
 * A second, sharper incident (Momo / Ningbo, 2026-08-26): a production
 * station tab needed ~5 QR scans before the operator gave up and manually
 * hard-refreshed — a passive dismissible toast is invisible to a gloved
 * operator glancing at a scanner screen from across the room.
 *
 * How: the build bakes an id into the bundle (`__BUILD_ID__`, vite define)
 * and emits the same id into an un-hashed `/version.json`. We re-fetch that
 * file when the tab regains focus (rate-limited) and on a slow interval.
 * Fetch failures are ignored — offline or a mid-deploy 404 must never nag.
 *
 * Two modes, chosen by the caller:
 * - `notify` (inventory app, desk users who may have unsaved form state):
 *   re-raises a persistent sonner toast with a Refresh action every
 *   `NOTIFY_REPEAT_MS` using a stable toast id (replaces rather than
 *   stacks). Never force-reloads.
 * - `auto-reload` (production stations, stateless between scans): once
 *   skew is detected the state latches (no further polling) and a 1s
 *   ticker counts down `RELOAD_COUNTDOWN_SECONDS`, gated on
 *   `isSafeToReload(document)`. The scanner-input carve-out inside that
 *   predicate is what makes this safe to ship: `[data-scanner-input]` is
 *   always focused by design at an idle station, so treating "focused with
 *   a residual value" as "typing" would block the reload forever. The
 *   countdown PAUSES (never resets) while unsafe, so an operator who keeps
 *   opening dialogs still eventually gets reloaded.
 */

export interface VersionSkewState {
  skewDetected: boolean;
  safeToReload: boolean;
  secondsRemaining: number | null;
  /**
   * True when auto-reload is disabled for this skew: we already attempted a
   * reload for this remote build id and the skew survived it (or storage is
   * unreachable so we cannot bound the attempts). The banner stays up in
   * manual-refresh-only mode instead of looping the station.
   */
  manualOnly: boolean;
}

const IDLE_STATE: VersionSkewState = {
  skewDetected: false,
  safeToReload: true,
  secondsRemaining: null,
  manualOnly: false,
};

export function useVersionSkew(options: { mode: 'notify' | 'auto-reload' }): VersionSkewState {
  const { mode } = options;
  const [state, setState] = useState<VersionSkewState>(IDLE_STATE);
  const skewLatchedRef = useRef(false);
  const lastCheckRef = useRef(0);
  const notifyCountRef = useRef(0);
  const lastNotifyAtRef = useRef(0);
  const secondsRef = useRef<number>(RELOAD_COUNTDOWN_SECONDS);
  const manualOnlyRef = useRef(false);
  const remoteBuildIdRef = useRef<string | null>(null);
  const reloadFiredRef = useRef(false);

  // A reload mid-mutation is the duplicate-print / missing-print ambiguity
  // the stations work hard to avoid — pause the countdown while any
  // react-query mutation is in flight (print dispatch, catch, pack, ...).
  const isMutating = useIsMutating();
  const mutatingRef = useRef(0);
  mutatingRef.current = isMutating;

  useEffect(() => {
    // Dev serves no version.json and the id is per-process — skip entirely.
    if (import.meta.env.DEV) return;

    const raiseNotifyToast = () => {
      // Production stations own their own notice (VersionUpdateBanner in
      // ProductionLayout). A second, English, duration:Infinity toast at
      // top-center would sit over the scan-feedback zone — suppress here and
      // let the repeat interval retry once the user is off /production/*.
      if (window.location.pathname.startsWith('/production')) return;
      notifyCountRef.current += 1;
      lastNotifyAtRef.current = Date.now();
      const isRepeat = notifyCountRef.current > 1;
      const toastFn = isRepeat ? toast.warning : toast.info;
      toastFn('A new version of the IMS is available', {
        id: 'version-skew',
        description: 'Refresh to pick up the latest changes. This tab is running an older build.',
        duration: Infinity,
        action: {
          label: 'Refresh',
          onClick: () => window.location.reload(),
        },
      });
    };

    const check = async () => {
      if (skewLatchedRef.current) return;
      lastCheckRef.current = Date.now();
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (!hasSkew(__BUILD_ID__, data.buildId)) return;

        skewLatchedRef.current = true;
        remoteBuildIdRef.current = data.buildId ?? null;
        // Circuit breaker: if we already auto-reloaded once for this remote
        // build and the skew is still here, the reload did not take (cached
        // index.html, intermediary cache on the China path). Degrade to a
        // manual-refresh banner instead of reloading the station forever.
        manualOnlyRef.current =
          mode === 'auto-reload' && data.buildId
            ? hasAutoReloadFiredForBuild(window.sessionStorage, data.buildId)
            : false;
        secondsRef.current = RELOAD_COUNTDOWN_SECONDS;
        setState({
          skewDetected: true,
          safeToReload: isSafeToReload(document),
          secondsRemaining: manualOnlyRef.current ? null : RELOAD_COUNTDOWN_SECONDS,
          manualOnly: manualOnlyRef.current,
        });

        if (mode === 'notify') raiseNotifyToast();
      } catch {
        // Offline / transient — stay quiet, try again next cycle.
      }
    };

    const onFocus = () => {
      if (Date.now() - lastCheckRef.current > FOCUS_DEBOUNCE_MS) void check();
    };

    const checkIntervalMs = mode === 'auto-reload' ? AUTO_RELOAD_CHECK_INTERVAL_MS : NOTIFY_CHECK_INTERVAL_MS;
    const pollInterval = window.setInterval(() => void check(), checkIntervalMs);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    // notify mode: re-raise the toast every NOTIFY_REPEAT_MS once latched
    // (no once-only dead end — an ignored/missed notice comes back).
    let notifyRepeatInterval: number | undefined;
    if (mode === 'notify') {
      notifyRepeatInterval = window.setInterval(() => {
        if (skewLatchedRef.current && Date.now() - lastNotifyAtRef.current >= NOTIFY_REPEAT_MS) {
          raiseNotifyToast();
        }
      }, NOTIFY_REPEAT_MS);
    }

    // auto-reload mode: once latched, tick every second re-evaluating
    // safe-to-reload and counting down; unsafe pauses rather than resets.
    let countdownInterval: number | undefined;
    if (mode === 'auto-reload') {
      countdownInterval = window.setInterval(() => {
        if (!skewLatchedRef.current || manualOnlyRef.current || reloadFiredRef.current) return;
        const safe = mutatingRef.current === 0 && isSafeToReload(document);
        if (!safe) {
          // Pause (never reset); return prev unchanged when already paused
          // so a long-open dialog doesn't re-render once per second.
          setState((prev) => (prev.safeToReload ? { ...prev, safeToReload: false } : prev));
          return;
        }
        const next = secondsRef.current - 1;
        secondsRef.current = next;
        if (next <= 0) {
          // Reload decided OUTSIDE the setState updater (no side effects in
          // render-phase updaters), latched so it can only fire once.
          reloadFiredRef.current = true;
          if (remoteBuildIdRef.current) {
            markAutoReloadFiredForBuild(window.sessionStorage, remoteBuildIdRef.current);
          }
          setState((prev) => ({ ...prev, safeToReload: true, secondsRemaining: 0 }));
          window.location.reload();
          return;
        }
        setState((prev) => ({ ...prev, safeToReload: true, secondsRemaining: next }));
      }, 1000);
    }

    return () => {
      window.clearInterval(pollInterval);
      if (notifyRepeatInterval !== undefined) window.clearInterval(notifyRepeatInterval);
      if (countdownInterval !== undefined) window.clearInterval(countdownInterval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [mode]);

  return state;
}

/** Thin wrapper preserving the original void-returning contract (App.tsx call site). */
export function useVersionSkewNotice(): void {
  useVersionSkew({ mode: 'notify' });
}
